/**
 * Billing Selectors
 * NgRx selectors for billing state
 */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { BillingState } from './billing.reducer';
import { PlanTier, SubscriptionStatus } from '../models/billing.models';

export const selectBillingState = createFeatureSelector<BillingState>('billing');

// Basic Selectors
export const selectPlans = createSelector(
  selectBillingState,
  (state) => state.plans
);

export const selectSubscription = createSelector(
  selectBillingState,
  (state) => state.subscription
);

export const selectUsage = createSelector(
  selectBillingState,
  (state) => state.usage
);

export const selectLoading = createSelector(
  selectBillingState,
  (state) => state.loading
);

export const selectCreating = createSelector(
  selectBillingState,
  (state) => state.creating
);

export const selectUpdating = createSelector(
  selectBillingState,
  (state) => state.updating
);

export const selectCanceling = createSelector(
  selectBillingState,
  (state) => state.canceling
);

export const selectError = createSelector(
  selectBillingState,
  (state) => state.error
);

// Computed Selectors
export const selectCurrentPlan = createSelector(
  selectPlans,
  selectSubscription,
  (plans, subscription) => {
    if (!subscription) return null;
    return plans.find(p => p.tier === subscription.plan_tier) || null;
  }
);

export const selectIsFreePlan = createSelector(
  selectSubscription,
  (subscription) => subscription?.plan_tier === PlanTier.FREE
);

export const selectIsActivePlan = createSelector(
  selectSubscription,
  (subscription) => subscription?.status === SubscriptionStatus.ACTIVE
);

export const selectIsTrialing = createSelector(
  selectSubscription,
  (subscription) => subscription?.status === SubscriptionStatus.TRIALING
);

export const selectIsCanceled = createSelector(
  selectSubscription,
  (subscription) => subscription?.status === SubscriptionStatus.CANCELED
);

export const selectCancelAtPeriodEnd = createSelector(
  selectSubscription,
  (subscription) => subscription?.cancel_at_period_end || false
);

export const selectTrialDaysRemaining = createSelector(
  selectSubscription,
  (subscription) => {
    if (!subscription?.trial_end) return null;
    const now = new Date();
    const trialEnd = new Date(subscription.trial_end);
    const diff = trialEnd.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }
);

export const selectUsagePercentages = createSelector(
  selectUsage,
  (usage) => {
    if (!usage) return null;
    return {
      apiCalls: usage.api_calls_percentage,
      documents: usage.documents_percentage,
      storage: usage.storage_percentage
    };
  }
);

export const selectIsApproachingLimit = createSelector(
  selectUsagePercentages,
  (percentages) => {
    if (!percentages) return false;
    return Object.values(percentages).some(p => p >= 80);
  }
);

export const selectHasExceededLimit = createSelector(
  selectUsagePercentages,
  (percentages) => {
    if (!percentages) return false;
    return Object.values(percentages).some(p => p >= 100);
  }
);

export const selectCanUpgrade = createSelector(
  selectSubscription,
  (subscription) => {
    if (!subscription) return true;
    return subscription.plan_tier !== PlanTier.ENTERPRISE;
  }
);

export const selectCanDowngrade = createSelector(
  selectSubscription,
  (subscription) => {
    if (!subscription) return false;
    return subscription.plan_tier !== PlanTier.FREE;
  }
);

export const selectBillingPeriodDaysRemaining = createSelector(
  selectSubscription,
  (subscription) => {
    if (!subscription?.current_period_end) return null;
    const now = new Date();
    const periodEnd = new Date(subscription.current_period_end);
    const diff = periodEnd.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }
);

export const selectAnyLoading = createSelector(
  selectLoading,
  selectCreating,
  selectUpdating,
  selectCanceling,
  (loading, creating, updating, canceling) => loading || creating || updating || canceling
);

